import {unwindEdges} from '@good-idea/unwind-edges'
import {
	Cart,
	CartLine,
	CartLineUpdateInput,
	MoneyV2,
	AttributeInput,
	CartLineInput,
	MailingAddress,
} from '@local/types/storefront-api-types'
import {getLocaleData} from '@local/utils/locale'
import {deleteWithLocale, fetchWithLocale, patchWithLocale, postWithLocale, putWithLocale} from '../useShopifyStore'
import {clientLocale} from '@local/stores/contexts/LocaleContextProvider'

export interface ShopifyCartProps {
	ready: boolean,
	cart?: Cart,
	cartId?: string,
	lines?: CartLine[],
	subtotal?: MoneyV2,
	total?: MoneyV2,
	itemsCount?: number,
	setCart: (cart) => Cart,
	initCart: () => void,
	createCart: (lines: CartLineInput[], country?:string) => Promise<Cart>,
	updateDiscountCodes: (discounts: string[]) => Promise<Cart>,
	addLines: (lines: CartLineInput[], removeDuplicates?: boolean) => Promise<Cart>,
	addLine: (variantId: string, quantity?: number, attributes?: AttributeInput[], addIntoLine?: boolean, sellingPlanId?: string, ifNotInCart?: boolean) => Promise<Cart>,
	updateLines: (lines: CartLineUpdateInput[]) => Promise<Cart>,
	updateLine: (id: string, merchandiseId: string, quantity?: number, attributes?: AttributeInput[], sellingPlanId?: string) => Promise<Cart>,
	removeLines: (lineItemIds: string[]) => Promise<Cart>,
	removeLine: (variantId: string) => Promise<Cart>,
	addRemoveLines: (addLineItemIds: string[], removeLineItemIds: string[]) => Promise<Cart>,
	removeSellingPlanLine: () => Promise<any>,
	setBuyerIdentity: (buyerIdentity: {countryCode?: string | null, customerAccessToken?: string | null, email?: string | null, phone?: string | null}) => Promise<any>,
	getAugmentedCheckoutUrl: () => string
}

export const shopifyCartStore = (set, get, store): ShopifyCartProps => ({
	ready: false,

	initCart: async () => {
		const cartId = get().cartId

		if (cartId) {
			set({loading: true})
			try {
				const cart = await fetchWithLocale('/api/shopify/cart', {searchParams: {cartId}})
				await get().setCart(cart)
			} catch (error) {
				console.warn('Could not restore cart', error)
				// create a new cart in case of trouble
				await get().createCart()
			}
		}

		// watch for future changes
		store.subscribe((state) => state.customer, async (customer) => {
			if (get().cart) {
				const {customerAccessToken, email, phone, countryCode, deliveryAddressPreferences} = get().cart?.buyerIdentity || {}
				const existingCart = {customerAccessToken, email, phone, countryCode, deliveryAddressPreferences}
				const buyerIdentity = {
					...existingCart,
					customerAccessToken: get().customerAccessToken?.accessToken,
					email: customer?.email,
					phone: customer?.phone,
					countryCode: getLocaleData(clientLocale.current, {uppercase: true}).countryCode,
					deliveryAddressPreferences: get().addresses?.map((address: MailingAddress) => ({
						customerAddressId: address.id,
					})),
				}
				await get().setBuyerIdentity(buyerIdentity)
			}
		}, {fireImmediately: true})
	},

	setCart: cart => {
		if (cart) {
			const current = (cart?.id === get().cartId && get().cart) || {}
			const {lines, cost, ...rest} = cart
			const unwoundLines = unwindEdges(lines)[0] as CartLine[]

			set({
				cartId: cart?.id,
				cart: {
					...current,
					...rest,
				},
				lines: unwoundLines,
				subtotal: cost.subtotalAmount,
				total: cost.totalAmount,
				itemsCount: unwoundLines.reduce((count, curr) => count + curr.quantity, 0),
				ready: true,
				loading: false,
			})
			return rest
		}

		set({
			cartId: undefined,
			cart: null,
			lines: null,
			subtotal: null,
			total: null,
			itemsCount: null,
			loading: false,
		})

		return null
	},

	createCart: async (lines: CartLineInput[] = [], country?:string) => {
		set({loading: true})

		const cart = await postWithLocale('/api/shopify/cart', {input: {lines}, country}, {locale: clientLocale.current})
		return get().setCart(cart)
	},

	updateDiscountCodes: async (discountCodes) => {
		const cartId = get().cartId

		set({loading: true})
		const cart = await postWithLocale('/api/shopify/cart/discounts', {cartId, discountCodes})

		if (cart) {
			return get().setCart(cart)
		} else {
			set({loading: false})
		}
	},

	addLine: async (merchandiseId, quantity = 1, attributes, addIntoLine = true, sellingPlanId, ifNotInCart = false) => {
		const lines = get().lines

		// we do not allow to add multiple plans
		const sellingPlan = lines?.find(line => line.sellingPlanAllocation)
		const removeIds: string[] = []
		if (sellingPlan && sellingPlanId) {
			if (sellingPlan.sellingPlanAllocation?.sellingPlan?.id === sellingPlanId) {
				// if selling plan exists, do nothing
				return false
			} else {
				// if selling plan is different, remove current
				removeIds.push(sellingPlan.id)
				// await get().removeLine(sellingPlan.id)
			}
		}

		const line = lines?.find(line => line.merchandise.id === merchandiseId)
		if (ifNotInCart && line) {
			set({loading: false})
			return
		}

		if (addIntoLine && line) {
			// if we have a line with variant, update that instead
			return await get().updateLine(line.id, line.merchandise.id, line.quantity + quantity, attributes, sellingPlanId)
		}

		if (removeIds.length) {
			return await get().addRemoveLines([{merchandiseId, quantity, attributes, sellingPlanId}], removeIds)
		} else {
			return await get().addLines([{merchandiseId, quantity, attributes, sellingPlanId}])
		}
	},

	addLines: async (lines, removeDuplicates = false) => {
		const cartId = get().cartId
		if (!cartId) {
			return await get().createCart(lines)
		}

		const _lines = get().lines
		if (removeDuplicates) {
			if (_lines) {
				lines = lines.filter(line => {
					return !_lines?.find(_line => _line.merchandise.id === line.merchandiseId)
				})
			}
			if (!lines.length) return get().cart
		}

		set({loading: true})
		const cart = await postWithLocale('/api/shopify/cart/lines', {cartId, lines})

		return get().setCart(cart)
	},

	addRemoveLines: async (addLines, removeLineIds) => {
		const cartId = get().cartId
		if (!cartId) {
			return await get().createCart(addLines)
		}

		set({loading: true})
		const cart = await patchWithLocale('/api/shopify/cart/lines', {cartId, addLines, removeLineIds})
		return get().setCart(cart)
	},

	updateLine: async (id, merchandiseId, quantity = 1, attributes, sellingPlanId) => {
		const line = get().lines?.filter(line => line.merchandise.id === merchandiseId)
		// const gift = get().lines?.find(line => line.merchandise.id === merchandiseId && parseFloat(line.cost.totalAmount.amount) === 0)

		// we do not allow to modify selling plan quantity
		if (line?.sellingPlanAllocation) return false

		// merge lines before update
		// if (lines.length > 1) {
		// 	const total = lines.reduce((previousValue, currentValue) => {
		// 		return previousValue.quantity + currentValue.quantity
		// 	})

		// 	const mod = lines.map((line, i) => {
		// 		if (!i) {
		// 			return {id: line.id, merchandiseId, quantity: total + quantity, attributes, sellingPlanId}
		// 		} else {
		// 			return {id: line.id, merchandiseId, quantity: 0, attributes, sellingPlanId}
		// 		}
		// 	})
		// 	console.log('useshopifyCartSlice.ts', total, mod)

		// 	return await get().updateLines(mod)
		// }

		return await get().updateLines([
			{id, merchandiseId, quantity, attributes, sellingPlanId},
		])
	},

	updateLines: async lines => {
		set({loading: true})
		const cartId = get().cartId
		const cart = await putWithLocale('/api/shopify/cart/lines', {cartId, lines})
		return get().setCart(cart)
	},

	removeLine: async (lineId) => {
		return await get().removeLines([lineId])
	},

	removeLines: async (lineIds) => {
		set({loading: true})
		const cartId = get().cartId
		const cart = await deleteWithLocale('/api/shopify/cart/lines', {cartId, lineIds})
		return get().setCart(cart)
	},

	removeSellingPlanLine: async () => {
		const lines = get().lines

		const sellingPlanLine = lines?.find(line => line.sellingPlanAllocation)
		if (sellingPlanLine) {
			return await get().removeLine(sellingPlanLine.id)
		}
	},

	setBuyerIdentity: async (buyerIdentity) => {
		const cartId = get().cartId
		if (buyerIdentity && cartId) {
			const cart = await postWithLocale('/api/shopify/cart/buyer-identity', {buyerIdentity, cartId})
			return get().setCart(cart)
		}
	},

	// useful for non-Plus customers,
	// with Multipass we have persisted user sign-in state
	getAugmentedCheckoutUrl: () => {
		let checkoutUrl = get().cart?.checkoutUrl
		const cart = get().cart
		const customer = get().customer

		const addresses = get().addresses
		const defaultAddressId = get().defaultAddressId
		const defaultAddress = addresses?.find(address => address.id?.split('?')[0] === defaultAddressId?.split('?')[0])

		if (cart?.checkoutUrl) {
			const url = new URL(cart?.checkoutUrl)

			if (customer?.email) {
				url.searchParams.append(`checkout[email]`, customer.email)
			}

			if (defaultAddress) {
				for (const [key, value] of Object.entries(defaultAddress)) {
					if (value && typeof value === 'string' && !['id', 'name'].includes(key)) {
						if (key === 'firstName') {
							url.searchParams.append(`checkout[shipping_address][first_name]`, value.toString())
						} else if (key === 'lastName') {
							url.searchParams.append(`checkout[shipping_address][last_name]`, value.toString())
						} else {
							url.searchParams.append(`checkout[shipping_address][${key.toLowerCase()}]`, value.toString())
						}
					}
				}
			}

			checkoutUrl = url.toString()
		}

		return checkoutUrl
	},
})
